// Core packages
import { formatDate } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

// Third party packages
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from "file-saver";
import { combineLatest, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, startWith, tap } from 'rxjs/operators';

// Core packages
import { AccessLogsService } from 'src/app/modules/access-logs/access-logs.service';
import { AuthService } from 'src/app/modules/auth/auth.service';
import { RolesService } from 'src/app/modules/roles/roles.service';
import { ListSetting } from 'src/app/shared/interfaces/list-setting.interface';
import { IUser } from 'src/app/shared/models/user/user.interface';
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from 'src/app/shared/services/helper.service';
import { ListsService } from 'src/app/shared/services/lists.service';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { AccessLogsDataSource } from 'src/app/shared/utils/data-sources/access-logs-data-source';

@Component({
  selector: 'app-access-logs-list',
  templateUrl: './access-logs-list.page.html',
  styleUrls: ['./access-logs-list.page.scss']
})
export class AccessLogsListComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  // BEGIN - Table properties
  displayedColumns: string[] = ['index', 'user', 'email', 'company', 'type', 'role', 'createdAt'];
  dataSource: AccessLogsDataSource;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  // @ViewChild('input', { static: true }) input: ElementRef;
  userFilter: FormControl = new FormControl('');
  typeFilter: FormControl = new FormControl('');
  emailFilter: FormControl = new FormControl('');
  companyFilter: FormControl = new FormControl('');
  ipAddressFilter: FormControl = new FormControl('');
  roleFilter: FormControl = new FormControl('');
  createdAtFilter: FormControl = new FormControl('');
  // END - Table properties

  activeRoute: string;
  filterRoleTypes: any[] = [];

  /**
   * Class constructor
   */
  constructor(
    private accessLogsService: AccessLogsService,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private listsService: ListsService,
    private rolesService: RolesService,
    private configService: ConfigService,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(this.translateService.instant('accessLogs.list.pageTitle'));

    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => {
        this.loggedUser = res
        if (this.loggedUser) {
          // current role
          const currentRole = this.loggedUser.userRoles.find((obj: any) => {
            return (
              obj._id.toString() ===
              this.authService.loggedUserRequestedRole$.getValue().toString()
            );
          });

          if ((currentRole.type === "admin"
            || currentRole.type === "operator")) {
            this.filterRoleTypes = this.rolesService.roleTypes;
          } else {
            this.filterRoleTypes = this.rolesService.roleTypes.filter((elem: any) => {
              return elem.value !== "admin" && elem.value !== "operator";// && elem.value !== "external";
            });
          }
          this.roleFilter.reset();
        }
      })
    );
    this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(res => this.loggedUserPermissions = res));

    // Init data source
    this.dataSource = new AccessLogsDataSource(this.accessLogsService, this.helperService);

    // Subscribe to listsService and update this list settings
    this.subscribeToListsSettings('accessLogs');

  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => this.loadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through general search
    // this.subscriptions.push(
    //   fromEvent(this.input.nativeElement, 'keyup')
    //     .pipe(
    //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
    //       distinctUntilChanged(), // Eliminate duplicate values
    //       tap(() => {
    //         this.paginator.pageIndex = 0; // Reset page
    //         this.loadPage();
    //       })
    //     )
    //     .subscribe()
    // );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.userFilter.valueChanges.pipe(startWith(null as string)),
        this.typeFilter.valueChanges.pipe(startWith(null as string)),
        this.emailFilter.valueChanges.pipe(startWith(null as string)),
        this.companyFilter.valueChanges.pipe(startWith(null as string)),
        this.ipAddressFilter.valueChanges.pipe(startWith(null as string)),
        this.roleFilter.valueChanges.pipe(startWith(null as string)),
        this.createdAtFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(
          res => {
            // Load page only if at least one value is not null
            if (!res.every((value) => value === null)) {
              this.paginator.pageIndex = 0;
              this.loadPage();
            }
          }
        )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());

    // Push current list settings to the listsService
    this.listsService.setListSetting(
      'accessLogs',
      this.paginator.pageIndex.valueOf(),
      this.paginator.pageSize.valueOf(),
      {
        general: '', // this.input.nativeElement.value,
        user: this.userFilter.value,
        type: this.typeFilter.value,
        email: this.emailFilter.value,
        company: this.companyFilter.value,
        ipAddress: this.ipAddressFilter.value,
        role: this.roleFilter.value,
        createdAt: this.createdAtFilter.value,
      }
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: '', // this.input.nativeElement.value,
      user: this.userFilter.value,
      type: this.typeFilter.value,
      email: this.emailFilter.value,
      company: this.companyFilter.value,
      ipAddress: this.ipAddressFilter.value,
      role: this.roleFilter.value,
      createdAt: this.createdAtFilter.value ? new Date(this.createdAtFilter.value).toISOString() : undefined,
    };
    this.dataSource.loadItems(start, length, this.sort.active, this.sort.direction, search);
  }

  /**
   * Export companies in a file with given format
   *
   * @since 1.0.0
   *
   * @param format The format. Can be 'excel' or 'csv'
   */
  onExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('accessLogs.list.exportFileName')} ${date}`;
    this.subscriptions.push(
      this.accessLogsService.getExport('').subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.userFilter.setValue(null);
    this.typeFilter.setValue(null);
    this.emailFilter.setValue(null);
    this.companyFilter.setValue(null);
    this.ipAddressFilter.setValue(null);
    this.roleFilter.setValue(null);
    this.createdAtFilter.setValue(null);


    // Reset tables' sorting versus and column
    this.sort.direction = 'desc';
    this.sort.active = 'createdAt';
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe(
        (settings: ListSetting[]) => {
          if (settings.length) {
            settings.forEach((setting: ListSetting) => {
              if (setting.name === name) {
                if (setting.pageIndex) {
                  this.paginator.pageIndex = setting.pageIndex;
                }
                if (setting.pageSize) {
                  this.paginator.pageSize = setting.pageSize;
                }
                if (setting.search && setting.search.general) {
                  // this.input.nativeElement.value = setting.search.general;
                }
                if (setting.search && setting.search.type) {
                  this.typeFilter.setValue(setting.search.type);
                }
                if (setting.search && setting.search.user) {
                  this.userFilter.setValue(setting.search.user);
                }
                if (setting.search && setting.search.email) {
                  this.emailFilter.setValue(setting.search.email);
                }
                if (setting.search && setting.search.company) {
                  this.companyFilter.setValue(setting.search.company);
                }
                if (setting.search && setting.search.ipAddress) {
                  this.ipAddressFilter.setValue(setting.search.ipAddress);
                }
                if (setting.search && setting.search.role) {
                  this.roleFilter.setValue(setting.search.role);
                }
                if (setting.search && setting.search.createdAt) {
                  this.createdAtFilter.setValue(setting.search.createdAt);
                }
              }
            });
          }
        }
      )
    );
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = '';
    const event = new KeyboardEvent('keyup');
    // this.input.nativeElement.dispatchEvent(event);
  }
}
